import { useMemo } from 'react';

import { useFinancialValueDisplay } from '@/shared/components/financial-values';
import {
    createChartFieldFormatter,
    formatChartPrimitive,
    resolveChartValueFormat,
} from '@/shared/components/modals/ResourceActionModals/EditReportComponentModal/helpers/formatters';
import { useReportQuery } from '@/shared/components/modals/ResourceActionModals/EditReportComponentModal/helpers/hook';
import { useDatasetSchema } from '@/shared/components/modals/ResourceActionModals/EditReportComponentModal/helpers/useDatasetSchema';
import { ReportComponent } from '@/types';
import { ReportComponentConfig } from '@/types/api/modules/business-intelligence';

import { humanizeChartLabel } from './chartPresentation';

export const useNivoReportFormatting = (component: ReportComponent) => {
    const config = component.configuration as ReportComponentConfig;
    const { preference: financialValueDisplay } = useFinancialValueDisplay();
    const query = useReportQuery(component);
    const { schema } = useDatasetSchema(config);

    const metricsByKey = useMemo(
        () => new Map((schema.metrics ?? []).map((metric) => [metric.key, metric])),
        [schema.metrics],
    );
    const dimensionsByKey = useMemo(
        () => new Map((schema.dimensions ?? []).map((dimension) => [dimension.key, dimension])),
        [schema.dimensions],
    );

    const getMetricDefinition = (slot = 0) => {
        const metric = query.metrics?.[slot];

        return metric ? metricsByKey.get(metric.field) : undefined;
    };

    const getDimensionDefinition = (slot = 0) => {
        const dimension = query.dimensions?.[slot];

        return dimension ? dimensionsByKey.get(dimension.field) : undefined;
    };

    const formatMetricValue = (value: number | string | null | undefined, slot = 0) => {
        const metric = query.metrics?.[slot];
        const metricDefinition = getMetricDefinition(slot);
        const format = resolveChartValueFormat({
            datasetId: query.datasetId,
            metricField: metric?.field,
            metricType: metricDefinition?.type,
            fieldType: metricDefinition?.type,
        });

        return formatChartPrimitive({ value, format, preference: financialValueDisplay });
    };

    const formatDimensionValue = (value: number | string | boolean | null | undefined, slot = 0) => {
        const dimensionDefinition = getDimensionDefinition(slot);
        const formatter = createChartFieldFormatter(dimensionDefinition, query.datasetId, financialValueDisplay);

        return formatter(value);
    };

    const getMetricLabel = (slot = 0) => {
        const metric = query.metrics?.[slot];
        const metricDefinition = getMetricDefinition(slot);

        return metric?.label ?? metricDefinition?.label ?? humanizeChartLabel(metric?.field);
    };

    const getDimensionLabel = (slot = 0) => {
        const dimension = query.dimensions?.[slot];
        const dimensionDefinition = getDimensionDefinition(slot);

        return dimension?.label ?? dimensionDefinition?.label ?? humanizeChartLabel(dimension?.field);
    };

    return {
        financialValueDisplay,
        query,
        formatMetricValue,
        formatDimensionValue,
        getMetricLabel,
        getDimensionLabel,
    };
};
